import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useParams, useSearchParams } from 'react-router';
import {
  LayoutDashboard,
  Users,
  CalendarPlus,
  Calendar,
  Video,
  ClipboardList,
  Archive,
  Settings,
  GraduationCap,
  Clapperboard,
  LogOut,
  ListChecks,
  UserCircle,
  CreditCard,
  type LucideIcon,
} from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import LeagueLogo from './components/LeagueLogo';
import SidebarBrandArea from './components/SidebarBrandArea';
import LeagueSwitcher from './components/LeagueSwitcher';
import { LeagueProvider, useLeague } from './hooks/useLeague';
import { LEAGUE_ROLE_MEMBER, leagueRoleBadgeClass, leagueRoleLabel } from './leagueRoles';
import { isMemberPreview, shellUsesOperatorNav, withPreviewQuery } from './stream/streamPages';
import LoadingState from './components/LoadingState';
import ErrorBanner from './components/ErrorBanner';
import {
  leagueNavLabels,
  buildLeagueNavItems,
  shouldRenderLeagueSidebarNav,
  shouldShowMobileBottomNav,
  type LscLeagueNavKey,
} from './leagueNav';
import { isLeagueArchived } from './lifecycle';
import { lscPath, mainScoutlyticsHomeUrl } from './routing';
import { isLscCfbDynastySetupEnabled, isRttsEnabled } from '../config/featureFlags';
import { legalPageUrl } from '../legal/legalLinks';
import ArchivedLeagueBanner from './components/ArchivedLeagueBanner';
import LscFooter from './components/LscFooter';
import LscMobileBottomNav from './components/LscMobileBottomNav';
import LscMobileNavDrawer from './components/LscMobileNavDrawer';
import ReportIssueProvider from './components/support/ReportIssueContext';
import { ReportIssueShell, ReportIssueTrigger } from './components/support/ReportIssueTrigger';
import { CfbSchoolCatalogProvider } from './hooks/useCfbSchoolCatalog';
import {
  LscContentColumnSetterContext,
  type LscContentColumn,
} from './lscContentColumn';

const LEAGUE_NAV_ICONS: Record<LscLeagueNavKey, LucideIcon> = {
  'command-center': LayoutDashboard,
  home: LayoutDashboard,
  setup: ListChecks,
  members: Users,
  import: CalendarPlus,
  'cfb-setup': GraduationCap,
  'cfb-schedule': Calendar,
  matches: Calendar,
  'needs-attention': ClipboardList,
  vods: Video,
  review: ClipboardList,
  archive: Archive,
  rtts: Clapperboard,
  settings: Settings,
};

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
    isActive
      ? 'bg-primary-900/50 text-primary-200 border border-primary-700'
      : 'text-secondary-300 hover:bg-secondary-800 hover:text-white',
  ].join(' ');
}

function LscLayoutContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [params] = useSearchParams();
  const [contentColumn, setContentColumn] = useState<LscContentColumn>('wide');
  const { user, logout } = useUser();
  const { league, membership, loading, error, isOperator, effectiveRole, leagues } = useLeague();
  const previewing = isMemberPreview(params.toString(), {
    isOwner: effectiveRole === 'owner',
    isCommissioner: isOperator,
    isOperator,
  });
  const shellOperator = shellUsesOperatorNav(isOperator, previewing);
  const badgeRole = previewing ? LEAGUE_ROLE_MEMBER : effectiveRole;

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  const leagueBase = leagueId ? lscPath(`/leagues/${leagueId}`) : null;
  const navLabels = leagueNavLabels(shellOperator);
  const archived = league ? isLeagueArchived(league.status) : false;

  const cfbSetupEnabled = isLscCfbDynastySetupEnabled();
  const showCfbNav = cfbSetupEnabled && league?.game_key === 'cfb';
  const showRtts = isRttsEnabled();

  const leagueNav = leagueBase
    ? buildLeagueNavItems({
        leagueBase,
        isCommissioner: shellOperator,
        showCfbNav,
        showRtts,
        archived,
      })
    : [];
  const showLeagueNav = shouldRenderLeagueSidebarNav({
    leagueId,
    hasLeague: Boolean(league),
    hasError: Boolean(error),
  });
  const showMobileBottomNav = shouldShowMobileBottomNav({
    leagueId,
    hasLeague: Boolean(league),
    hasError: Boolean(error),
    loading,
  });
  const wideColumn = contentColumn === 'wide';
  const columnClass = wideColumn ? 'max-w-lsc-wide mx-auto' : 'max-w-lsc-narrow mx-auto';

  return (
    <LscContentColumnSetterContext.Provider value={setContentColumn}>
    <div className="min-h-screen bg-secondary-900 text-white flex flex-col md:flex-row">
      <aside className="hidden md:flex md:w-64 md:flex-col md:border-r border-secondary-700 bg-secondary-900/95 shrink-0">
        <SidebarBrandArea league={league} showLeagueBadge={showLeagueNav && Boolean(league)} />

        <nav className="p-4 space-y-1" aria-label="Stream Center navigation">
          <NavLink to={lscPath('/leagues')} className={navClass}>
            <LayoutDashboard className="h-4 w-4" aria-hidden />
            Leagues
          </NavLink>

          {user && (
            <NavLink to={lscPath('/settings')} className={navClass} data-testid="account-settings-nav">
              <UserCircle className="h-4 w-4" aria-hidden />
              Account settings
            </NavLink>
          )}

          {user && (
            <NavLink to={lscPath('/pricing')} className={navClass} data-testid="lsc-pricing-nav">
              <CreditCard className="h-4 w-4" aria-hidden />
              Pricing
            </NavLink>
          )}

          {user && (
            <NavLink to={lscPath('/billing')} className={navClass} data-testid="lsc-billing-nav">
              <CreditCard className="h-4 w-4" aria-hidden />
              Billing
            </NavLink>
          )}

          {user && (
            <ReportIssueTrigger
              variant="nav"
              testId="lsc-report-issue-nav"
              prefill={{ surface: 'lsc_sidebar' }}
            />
          )}

          {showLeagueNav && (
            <>
              <div className="pb-2" data-testid="lsc-league-workspace-switcher">
                <LeagueSwitcher />
              </div>
              <div className="hidden md:block space-y-1" data-testid="lsc-desktop-league-nav">
                {loading ? (
                  <p className="px-3 py-2 text-xs text-secondary-500">Loading league access...</p>
                ) : (
                  leagueNav.map(({ to, label, navKey, end }) => {
                    const Icon = LEAGUE_NAV_ICONS[navKey];
                    const href = previewing ? withPreviewQuery(to, params.toString()) : to;
                    return (
                      <NavLink key={to} to={href} end={end} className={navClass}>
                        <Icon className="h-4 w-4" aria-hidden />
                        {label}
                      </NavLink>
                    );
                  })
                )}
                <span className="sr-only" data-testid="league-nav-labels">
                  {navLabels.join(',')}
                </span>
              </div>
            </>
          )}
          {leagueId && !showLeagueNav && loading && (
            <p className="px-3 py-2 text-xs text-secondary-500">Loading league access...</p>
          )}
        </nav>

        <div className="p-4 md:mt-auto border-t border-secondary-700 space-y-2 md:block">
          <div className="hidden md:block">
            {user && (
              <p className="text-xs text-secondary-500 truncate">
                {user.display_name || user.twitch_login}
                {membership && (
                  <span className="block capitalize text-secondary-400">{membership.role}</span>
                )}
              </p>
            )}
          </div>
          {user && (
            <button
              type="button"
              onClick={() => logout()}
              className="flex items-center gap-2 text-sm text-secondary-400 hover:text-red-300"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Sign out
            </button>
          )}
          <Link
            to={mainScoutlyticsHomeUrl()}
            className="hidden md:block text-xs text-secondary-500 hover:text-primary-300"
          >
            Back to Scoutlytics
          </Link>
          <div
            className="hidden md:flex flex-wrap gap-x-3 gap-y-1 text-xs text-secondary-600"
            data-testid="lsc-sidebar-legal-links"
          >
            <a
              href={legalPageUrl('terms')}
              className="hover:text-primary-300/90 transition-colors"
            >
              Terms
            </a>
            <a
              href={legalPageUrl('privacy')}
              className="hover:text-primary-300/90 transition-colors"
            >
              Privacy
            </a>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {leagueId && league && (
          <header
            className={[
              'border-b border-secondary-700 py-3 bg-secondary-900/80',
              wideColumn ? 'px-4 md:px-6' : 'px-4',
            ].join(' ')}
          >
            <div className={`${columnClass} flex flex-col gap-3`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {showLeagueNav && leagueId && (
                    <LscMobileNavDrawer
                      leagueId={leagueId}
                      leagueName={league.name}
                      leagueNav={
                        previewing
                          ? leagueNav.map((item) => ({
                              ...item,
                              to: withPreviewQuery(item.to, params.toString()),
                            }))
                          : leagueNav
                      }
                      userDisplayName={user?.display_name || user?.twitch_login}
                      onSignOut={() => logout()}
                    />
                  )}
                  <LeagueLogo league={league} size="sm" testId="league-header-logo" />
                  <div className="min-w-0 flex-1">
                    <h1 className="text-lg font-semibold text-white truncate">{league.name}</h1>
                    <p className="text-xs text-secondary-400">
                      {league.game_label ?? league.game_key ?? 'Competition league'}
                    </p>
                  </div>
                </div>
                {membership && (
                  <span
                    className={`rounded-full border border-primary-700 bg-primary-900/40 px-3 py-1 text-xs ${leagueRoleBadgeClass(badgeRole)}`}
                    data-testid="league-role-badge"
                  >
                    {leagueRoleLabel(badgeRole)}
                  </span>
                )}
              </div>
              {showLeagueNav && leagues.length > 1 ? (
                <div className="md:hidden min-w-0" data-testid="lsc-header-workspace-switcher">
                  <LeagueSwitcher compact showContextCopy={false} />
                </div>
              ) : null}
            </div>
          </header>
        )}

        <main
          className={[
            'flex-1 p-4 md:p-6',
            showMobileBottomNav ? 'pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] md:pb-6' : '',
          ].join(' ')}
        >
          <div className={`${columnClass} space-y-4`} data-testid="lsc-content-column" data-lsc-column={contentColumn}>
            {leagueId && league && archived && <ArchivedLeagueBanner />}
            {leagueId && loading && <LoadingState message="Loading league workspace..." />}
            {leagueId && error && !loading && <ErrorBanner message={error} />}
            <Outlet />
          </div>
        </main>
        <LscFooter />
        {showMobileBottomNav && leagueBase && leagueId && (
          <LscMobileBottomNav
            leagueId={leagueId}
            leagueBase={leagueBase}
            leagueName={league?.name}
            isCommissioner={shellOperator}
            preservePreview={previewing}
            archived={archived}
          />
        )}
      </div>
      <ReportIssueShell />
    </div>
    </LscContentColumnSetterContext.Provider>
  );
}

const LscLayout: React.FC = () => (
  <LeagueProvider>
    <ReportIssueProvider>
      <LscLayoutWithCatalog />
    </ReportIssueProvider>
  </LeagueProvider>
);

function LscLayoutWithCatalog() {
  const { league } = useLeague();
  const enableCfbCatalog = league?.game_key === 'cfb';

  return (
    <CfbSchoolCatalogProvider enabled={enableCfbCatalog}>
      <LscLayoutContent />
    </CfbSchoolCatalogProvider>
  );
}

export default LscLayout;
